"use client";

import { Button } from "@heroui/button";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";

import type { LanguageCode } from "@/config/i18n";
import type { Translator } from "@/lib/i18n/translations";
import type { MovieLibraryState } from "@/lib/library/state";
import SignInModal from "@/components/auth/SignInModal";
import { ConfirmUnwatchDialog } from "@/components/library/ConfirmUnwatchDialog";
import { RatingDialog } from "@/components/library/RatingDialog";
import {
  BookmarkIcon,
  CheckIcon,
  EyeIcon,
  StarIcon,
} from "@/components/library/icons";
import { useLibraryActions } from "@/components/library/useLibraryActions";
import { Tooltip } from "@/components/ui/Tooltip";
import { useAuth } from "@/lib/hooks/useAuth";
import { useLibrary, useMovieLibraryState } from "@/lib/hooks/useLibrary";
import { getLanguageFromPathname } from "@/lib/i18n/paths";
import { createTranslator } from "@/lib/i18n/translations";
import { localeFor } from "@/lib/library/view";

interface MovieActionsProps {
  movieId: string;
  movieTitle: string;
}

type Dialog = "rate" | "confirmUnwatch" | null;

/*
 * Layout: on phones the three controls are equal columns (icon above label),
 * from `sm` up a row of regular buttons. Every state - skeleton, signed out,
 * signed in - has the same height, so nothing moves when auth resolves.
 */
const GRID = "grid grid-cols-3 gap-2 sm:flex sm:flex-wrap";
const ACTION_BUTTON =
  "h-[4.5rem] min-w-0 flex-col gap-1 whitespace-normal px-1 py-2 text-center text-xs leading-tight sm:h-10 sm:flex-row sm:gap-2 sm:whitespace-nowrap sm:px-4 sm:text-small";
const SECOND_ROW = "flex h-10 items-center gap-2";

/**
 * Watchlist / watched / rating controls on a movie's page.
 * Signed-out visitors see the same controls, which invite them to sign in.
 */
export function MovieActions({ movieId, movieTitle }: MovieActionsProps) {
  const pathname = usePathname();
  const lang = getLanguageFromPathname(pathname);
  const t = createTranslator(lang);
  const { status: authStatus } = useAuth();
  const { status: libraryStatus } = useLibrary();
  const movie = useMovieLibraryState(movieId);
  const actions = useLibraryActions();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [isSignInOpen, setSignInOpen] = useState(false);

  if (authStatus === "loading") return <MovieActionsSkeleton />;

  if (authStatus === "anonymous") {
    return (
      <SignedOutActions
        isSignInOpen={isSignInOpen}
        lang={lang}
        pathname={pathname}
        t={t}
        onSignInOpenChange={setSignInOpen}
      />
    );
  }

  const ref = { id: movieId, title: movieTitle };
  // Without any cached data the state is unknown for a moment: don't let
  // the user act on a guess.
  const isLoading = libraryStatus === "loading";

  const toggleWatchlist = () =>
    movie.inWatchlist
      ? actions.removeFromWatchlist(ref)
      : actions.addToWatchlist(ref);

  const toggleWatched = () => {
    if (!movie.watched) return actions.markWatched(ref);
    if (movie.rating !== null) return setDialog("confirmUnwatch");

    return actions.unmarkWatched(ref);
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        aria-busy={isLoading || undefined}
        aria-label={t("library.movieActions.label")}
        className={GRID}
        role="group"
      >
        <Button
          aria-label={
            movie.inWatchlist
              ? t("userPages.actions.inWatchlistLabel")
              : undefined
          }
          className={ACTION_BUTTON}
          color={movie.inWatchlist ? "primary" : "default"}
          isDisabled={isLoading}
          startContent={<BookmarkIcon filled={movie.inWatchlist} />}
          variant={movie.inWatchlist ? "solid" : "bordered"}
          onPress={() => void toggleWatchlist()}
        >
          {movie.inWatchlist
            ? t("userPages.actions.inWatchlist")
            : t("userPages.actions.addToWatchlist")}
        </Button>

        <Button
          aria-label={
            movie.watched ? t("userPages.actions.watchedLabel") : undefined
          }
          className={ACTION_BUTTON}
          color={movie.watched ? "success" : "default"}
          isDisabled={isLoading}
          startContent={movie.watched ? <CheckIcon /> : <EyeIcon />}
          variant={movie.watched ? "solid" : "bordered"}
          onPress={() => void toggleWatched()}
        >
          {movie.watched
            ? t("userPages.actions.watched")
            : t("userPages.actions.markAsWatched")}
        </Button>

        <Button
          aria-haspopup="dialog"
          aria-label={
            movie.rating !== null
              ? t("userPages.actions.ratedLabel", { rating: movie.rating })
              : undefined
          }
          className={ACTION_BUTTON}
          color={movie.rating !== null ? "warning" : "default"}
          isDisabled={isLoading}
          startContent={<StarIcon filled={movie.rating !== null} />}
          variant={movie.rating !== null ? "solid" : "bordered"}
          onPress={() => setDialog("rate")}
        >
          {movie.rating !== null
            ? t("userPages.actions.ratingValue", { rating: movie.rating })
            : t("userPages.actions.rateMovie")}
        </Button>
      </div>

      <LibraryStatusLine
        isLoading={isLoading}
        lang={lang}
        movie={movie}
        t={t}
      />

      <RatingDialog
        currentRating={movie.rating}
        isOpen={dialog === "rate"}
        isWatched={movie.watched}
        lang={lang}
        movieTitle={movieTitle}
        onOpenChange={(open) => setDialog(open ? "rate" : null)}
        onRemove={() => void actions.removeRating(ref)}
        onSave={(rating) => void actions.rate(ref, rating)}
      />

      {movie.rating !== null && (
        <ConfirmUnwatchDialog
          isOpen={dialog === "confirmUnwatch"}
          lang={lang}
          movieTitle={movieTitle}
          rating={movie.rating}
          onConfirm={() => void actions.unmarkWatched(ref)}
          onOpenChange={(open) => setDialog(open ? "confirmUnwatch" : null)}
        />
      )}
    </div>
  );
}

/** "You rated it 8/10 · Oct 1, 2026" + a link to the library. One line. */
function LibraryStatusLine({
  movie,
  isLoading,
  lang,
  t,
}: {
  movie: MovieLibraryState;
  isLoading: boolean;
  lang: LanguageCode;
  t: Translator;
}) {
  // Rendered only after auth resolved (client-side), so the user's time
  // zone can be used without hydration mismatches.
  const format = (iso: string) =>
    new Intl.DateTimeFormat(localeFor(lang), { dateStyle: "medium" }).format(
      new Date(iso),
    );
  let status: string;
  let view: "watchlist" | "watched" | "ratings" = "watchlist";

  if (isLoading) {
    status = t("library.movieActions.loading");
  } else if (movie.rating !== null && movie.ratedAt) {
    status = t("library.movieActions.statusRated", {
      rating: movie.rating,
      date: format(movie.ratedAt),
    });
    view = "ratings";
  } else if (movie.watched && movie.watchedAt) {
    status = t("library.movieActions.statusWatched", {
      date: format(movie.watchedAt),
    });
    view = "watched";
  } else if (movie.inWatchlist && movie.addedAt) {
    status = t("library.movieActions.statusWatchlist", {
      date: format(movie.addedAt),
    });
  } else {
    status = t("library.movieActions.statusNone");
  }

  return (
    <p className={`${SECOND_ROW} text-sm text-default-500`}>
      <span className="min-w-0 truncate" data-testid="library-status">
        {status}
      </span>
      <span aria-hidden="true">·</span>
      <Link
        className="shrink-0 rounded-small text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-focus"
        href={`/${lang}/${view}`}
      >
        {t("library.movieActions.openLibrary")}
      </Link>
    </p>
  );
}

interface SignedOutActionsProps {
  t: Translator;
  lang: LanguageCode;
  pathname: string;
  isSignInOpen: boolean;
  onSignInOpenChange: (open: boolean) => void;
}

/** The same controls, each opening the sign-in popover. */
function SignedOutActions({
  t,
  lang,
  pathname,
  isSignInOpen,
  onSignInOpenChange,
}: SignedOutActionsProps) {
  const openSignIn = () => onSignInOpenChange(true);
  const controls = [
    {
      key: "watchlist",
      label: t("userPages.actions.addToWatchlist"),
      hint: t("userPages.actions.signInToWatchlist"),
      icon: <BookmarkIcon />,
    },
    {
      key: "watched",
      label: t("userPages.actions.markAsWatched"),
      hint: t("userPages.actions.signInToMarkWatched"),
      icon: <EyeIcon />,
    },
    {
      key: "rate",
      label: t("userPages.actions.rateMovie"),
      hint: t("userPages.actions.signInToRate"),
      icon: <StarIcon />,
    },
  ];

  return (
    <div className="flex flex-col gap-2">
      <div
        aria-label={t("library.movieActions.label")}
        className={GRID}
        role="group"
      >
        {controls.map((control) => (
          <Tooltip key={control.key} content={control.hint}>
            <Button
              className={ACTION_BUTTON}
              startContent={control.icon}
              variant="bordered"
              onPress={openSignIn}
            >
              {control.label}
            </Button>
          </Tooltip>
        ))}
      </div>
      <div className={SECOND_ROW}>
        <SignInModal
          buttonClassName="h-10 w-full sm:w-auto"
          buttonText={t("library.movieActions.signIn")}
          currentLang={lang}
          isOpen={isSignInOpen}
          redirectPath={pathname}
          onOpenChange={onSignInOpenChange}
        />
      </div>
    </div>
  );
}

/** Shown only while we don't know yet whether the visitor is signed in. */
function MovieActionsSkeleton() {
  const block = "animate-pulse rounded-medium bg-default-100";

  return (
    <div aria-hidden="true" className="flex flex-col gap-2">
      <div className={GRID}>
        <div className={`${block} h-[4.5rem] sm:h-10 sm:w-40`} />
        <div className={`${block} h-[4.5rem] sm:h-10 sm:w-40`} />
        <div className={`${block} h-[4.5rem] sm:h-10 sm:w-24`} />
      </div>
      <div className={SECOND_ROW}>
        <div className={`${block} h-5 w-56 max-w-full`} />
      </div>
    </div>
  );
}
